import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { useToast } from '../../ToastContext';
import { fmtDate } from '../../dateUtil';
import { formatCurrency } from '../../currencyUtil';

const STATUS = {
  draft:    { color: 'text-amber-600 dark:text-amber-400',     dot: 'bg-amber-400',   label: 'Draft' },
  sent:     { color: 'text-blue-600 dark:text-blue-400',       dot: 'bg-blue-500',    label: 'Sent' },
  complete: { color: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500', label: 'Complete' },
  archived: { color: 'text-slate-500 dark:text-slate-400',     dot: 'bg-slate-400',   label: 'Archived' },
};

function DealTable({ deals, onDelete, onArchive }) {
  const navigate = useNavigate();
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Deal</th>
          <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Status</th>
          <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider hidden md:table-cell">Type</th>
          <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider hidden md:table-cell">Pickup / Delivery</th>
          <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider hidden lg:table-cell">End Date</th>
          <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Total</th>
          <th className="w-20 px-4 py-3" />
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
        {deals.map((deal) => {
          const s = STATUS[deal.status] || STATUS.draft;
          return (
            <tr
              key={deal.uuid}
              className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors group"
            >
              <td className="px-4 py-3.5">
                <button
                  onClick={() => navigate(`/seller/deals/${deal.uuid}`)}
                  className="font-medium text-slate-800 dark:text-slate-100 text-left hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                >
                  {deal.name || `Deal #${deal.id}`}
                </button>
              </td>
              <td className="px-4 py-3.5">
                <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${s.color}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${s.dot} shrink-0`} />
                  {s.label}
                </span>
              </td>
              <td className="px-4 py-3.5 hidden md:table-cell text-slate-600 dark:text-slate-400">
                {deal.transaction_type || '—'}
              </td>
              <td className="px-4 py-3.5 hidden md:table-cell">
                <span className="capitalize text-slate-600 dark:text-slate-400">{deal.pickup_or_delivery || '—'}</span>
              </td>
              <td className="px-4 py-3.5 hidden lg:table-cell text-slate-600 dark:text-slate-400">
                {deal.end_date ? fmtDate(deal.end_date) : '—'}
              </td>
              <td className="px-4 py-3.5 text-right font-medium tabular-nums text-slate-800 dark:text-slate-200">
                {deal.total_price ? `$${formatCurrency(deal.total_price)}` : '—'}
              </td>
              <td className="px-4 py-3.5 text-right">
                <div className="flex items-center justify-end gap-1 md:opacity-0 md:group-hover:opacity-100 transition-all">
                  {deal.status === 'complete' && (
                    <button
                      onClick={(e) => onArchive(e, deal)}
                      title="Archive"
                      className="w-8 h-8 md:w-6 md:h-6 flex items-center justify-center rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                      </svg>
                    </button>
                  )}
                  {(deal.status === 'draft' || deal.status === 'sent') && (
                    <button
                      onClick={(e) => onDelete(e, deal.uuid, deal.status)}
                      title={deal.status === 'draft' ? 'Delete' : 'Retract'}
                      className="w-8 h-8 md:w-6 md:h-6 flex items-center justify-center rounded text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default function SellerDealList() {
  const [deals, setDeals]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    let mounted = true;
    api.get('/deals')
      .then(d  => { if (mounted) setDeals(d); })
      .catch(e => { if (mounted) setError(e.message); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const deleteDeal = async (e, uuid, status) => {
    e.stopPropagation();
    const msg = status === 'sent'
      ? 'Retract this deal? It will be removed from all buyers. This cannot be undone if buyers have active transactions.'
      : 'Delete this deal?';
    if (!window.confirm(msg)) return;
    try {
      await api.del(`/deals/${uuid}`);
      setDeals(d => d.filter(x => x.uuid !== uuid));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const archiveDeal = async (e, deal) => {
    e.stopPropagation();
    try {
      await api.put(`/deals/${deal.uuid}`, { ...deal, status: 'archived' });
      setDeals(ds => ds.map(d => d.uuid === deal.uuid ? { ...d, status: 'archived' } : d));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const activeDeals    = deals
    .filter(d => d.status === 'draft' || d.status === 'sent')
    .sort((a, b) => (a.status === 'draft' ? 0 : 1) - (b.status === 'draft' ? 0 : 1));
  const completedDeals = deals.filter(d => d.status === 'complete' || (showArchived && d.status === 'archived'));
  const archivedCount  = deals.filter(d => d.status === 'archived').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-lg font-semibold text-slate-900 dark:text-white">Deals</h1>
        <button onClick={() => navigate('/seller/deals/new')} className="btn-primary">
          New Deal
        </button>
      </div>

      {!deals.length ? (
        <div className="card flex flex-col items-center justify-center py-20 text-center">
          <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center mb-3">
            <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">No deals yet</p>
          <p className="text-xs text-slate-400 dark:text-slate-500 mb-5">Create a deal to start sending vehicles to buyers.</p>
          <button onClick={() => navigate('/seller/deals/new')} className="btn-primary text-xs px-3 py-1.5">
            Create First Deal
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Active deals */}
          {activeDeals.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                <span className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Active</span>
                <span className="text-xs font-semibold text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">{activeDeals.length}</span>
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
              </div>
              <div className="card overflow-x-auto">
                <DealTable deals={activeDeals} onDelete={deleteDeal} onArchive={archiveDeal} />
              </div>
            </div>
          )}

          {/* Completed deals */}
          {(completedDeals.length > 0 || archivedCount > 0) && (
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Completed</span>
                  {archivedCount > 0 && (
                    <button
                      onClick={() => setShowArchived(a => !a)}
                      className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 border border-slate-200 dark:border-slate-700 rounded px-2 py-0.5 transition-colors"
                    >
                      {showArchived ? 'Hide archived' : `+${archivedCount} archived`}
                    </button>
                  )}
                </div>
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
              </div>
              {completedDeals.length > 0 ? (
                <div className="card overflow-x-auto">
                  <DealTable deals={completedDeals} onDelete={deleteDeal} onArchive={archiveDeal} />
                </div>
              ) : (
                <p className="text-center text-xs text-slate-400 py-4">No completed deals yet.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
